import { useState, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Image,
  SimpleGrid,
  IconButton,
  Progress,
  useColorModeValue,
  useToast,
  Button,
} from '@chakra-ui/react';
import { FiUpload, FiX, FiImage } from 'react-icons/fi';

const MAX_FILES = 5;
const MAX_SIZE_MB = 10;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const ImageUploader = ({ onFilesChange, maxFiles = MAX_FILES }) => {
  const [previews, setPreviews] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const dragBg = useColorModeValue('blue.50', 'blue.900');

  const processFiles = (newFiles) => {
    const files = Array.from(newFiles);
    const tooLarge = files.filter((f) => f.size > MAX_SIZE_MB * 1024 * 1024);
    if (tooLarge.length > 0) {
      toast({
        title: `${tooLarge.length === 1 ? 'File' : 'Files'} too large`,
        description: `${tooLarge.map((f) => f.name).join(', ')} exceeded the ${MAX_SIZE_MB}MB limit and was not added.`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
    const valid = files.filter((file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) return false;
      if (file.size > MAX_SIZE_MB * 1024 * 1024) return false;
      return true;
    });

    const remaining = maxFiles - previews.length;
    const toAdd = valid.slice(0, remaining);

    const newPreviews = toAdd.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name,
    }));

    const updated = [...previews, ...newPreviews];
    setPreviews(updated);
    onFilesChange(updated.map((p) => p.file));
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(previews[index].url);
    const updated = previews.filter((_, i) => i !== index);
    setPreviews(updated);
    onFilesChange(updated.map((p) => p.file));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFiles(e.dataTransfer.files);
  };

  return (
    <VStack spacing={4} align="stretch">
      {previews.length < maxFiles && (
        <Box
          border="2px dashed"
          borderColor={dragOver ? 'blue.400' : borderColor}
          rounded="lg"
          p={8}
          textAlign="center"
          bg={dragOver ? dragBg : 'transparent'}
          cursor="pointer"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          transition="all 0.2s"
          _hover={{ bg: hoverBg, borderColor: 'blue.300' }}
        >
          <VStack spacing={3}>
            <Icon as={FiImage} boxSize={10} color="gray.400" />
            <VStack spacing={1}>
              <Text fontWeight="medium">Drop images here or click to browse</Text>
              <Text fontSize="sm" color="gray.500">
                JPG, PNG, WEBP up to {MAX_SIZE_MB}MB each · Max {maxFiles} images
              </Text>
            </VStack>
            <Button size="sm" leftIcon={<Icon as={FiUpload} />} colorScheme="blue" variant="outline">
              Choose Files
            </Button>
          </VStack>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => processFiles(e.target.files)}
          />
        </Box>
      )}

      {previews.length > 0 && (
        <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={3}>
          {previews.map((preview, index) => (
            <Box key={index} position="relative" rounded="md" overflow="hidden">
              <Image
                src={preview.url}
                alt={preview.name}
                h="100px"
                w="full"
                objectFit="cover"
              />
              <IconButton
                icon={<Icon as={FiX} />}
                size="xs"
                colorScheme="red"
                position="absolute"
                top={1}
                right={1}
                rounded="full"
                onClick={(e) => { e.stopPropagation(); removeImage(index); }}
                aria-label="Remove image"
              />
              {index === 0 && (
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  right={0}
                  bg="blackAlpha.600"
                  px={2}
                  py={0.5}
                >
                  <Text fontSize="xs" color="white">Cover</Text>
                </Box>
              )}
            </Box>
          ))}
        </SimpleGrid>
      )}
    </VStack>
  );
};
